import React, { useCallback, useMemo, useState } from 'react';

import { sortBy } from 'lodash';
import { Icon } from '@ui/Icon';
import { Menu } from '@ui/menus/Menu';
import { KebabMenu } from '@ui/menus/KebabMenu';
import { NodonMenuItemProps } from '@ui/menus/menu.model';
import { IProduct, ProductID } from '@shared/models/product.model';
import { ProductCategoryElement } from '@shared/models/project.model';
import { openEditProductDialog } from '@features/products/EditProduct/EditProductDialog';
import { openProductSelector } from '@features/products/EditProduct/ProductSelector';
import {
  setLocallyStoredEPDIds,
  useLocallyStoredEPDIds,
} from '@features/products/use-product-switch.hooks';
import { useIsReadonlyOrganization } from '@client/store/organization';
import { useUpdateProduct } from '@client/store/product';
import { useUpdateProductInVersion } from '@features/products/use-products.hooks';
import amplitudeLog from '@client/amplitude';
import { AMP_ELEMENT_APPLY_EPD } from '@shared/constants';

/** Selection action emitted to consumers via {@link EPDMenuContentProps.onEpdSelect}. */
export type EpdSelectAction =
  { type: 'none'; id?: undefined } | { type: 'epd'; id: ProductID };

const UPDATE_PRODUCT_IN_VERSION_SERVER_OPTIONS = {
  skipServerUpdate: true,
} as const;

type EpdKebabMenuProps = {
  epd: IProduct;
  genericProductId?: ProductID;
  isReadonlyOrganization: (organizations: string[]) => boolean;
  onEdit: (epd: IProduct) => void;
  onRemove: (epd: IProduct) => void;
};

const EpdKebabMenu: React.FC<EpdKebabMenuProps> = ({
  epd,
  genericProductId,
  isReadonlyOrganization,
  onEdit,
  onRemove,
}) => {
  const [_, setIsOpen] = useState(false);
  if (!genericProductId) return null;
  const isTemplateItem = isReadonlyOrganization(epd.organizations ?? []);
  const disableRemove = isTemplateItem && epd.generic_id === genericProductId;
  const items: NodonMenuItemProps[] = [
    {
      id: 'epd-menu.edit',
      key: `epd-menu-edit-${epd.id}`,
      label: 'Edit EPD',
      icon: <Icon category="mui" icon="Edit" />,
      disabled: isTemplateItem,
      tooltip: isTemplateItem ? 'Cannot edit template EPD' : '',
      onClick: () => onEdit(epd),
    },
    {
      id: 'epd-menu.remove',
      key: `epd-menu-remove-${epd.id}`,
      label: 'Remove from list',
      icon: <Icon category="mui" icon="Clear" />,
      disabled: disableRemove,
      tooltip: disableRemove
        ? 'Cannot remove template EPD from its default generic product'
        : '',
      onClick: () => onRemove(epd),
    },
  ];
  if (items.length === 0) return null;
  return (
    <KebabMenu
      id={epd.id}
      items={items}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      margin={0}
    />
  );
};

type EPDMenuContentProps = {
  /** EPD items to render between None and New/Library actions. */
  epds: IProduct[];
  /** Selected EPD ids; empty=none, one=single, many=mixed. */
  selectedEpdIds: ProductID[];
  /** Parent element used when creating a new EPD. */
  productCategoryElement?: ProductCategoryElement;
  /** Generic product context for new EPD creation. */
  genericProductId?: ProductID;
  /** Selection callback for None or a specific EPD. */
  onEpdSelect: (action: EpdSelectAction) => void;
};

export const EPDMenuContent: React.FC<EPDMenuContentProps> = ({
  epds,
  selectedEpdIds,
  productCategoryElement,
  genericProductId,
  onEpdSelect,
}) => {
  const isReadonlyOrganization = useIsReadonlyOrganization();
  const updateProduct = useUpdateProduct();
  const updateProductInVersion = useUpdateProductInVersion();
  const updateProductInVersionLocally = useUpdateProductInVersion(
    UPDATE_PRODUCT_IN_VERSION_SERVER_OPTIONS,
  );
  const epdIdsAppliedToGenericProduct = useLocallyStoredEPDIds(
    genericProductId ?? '',
  );
  const selection = useMemo<{
    id?: ProductID;
    mode: 'none' | 'single' | 'mixed';
  }>(() => {
    if (selectedEpdIds.length === 0) return { mode: 'none' };
    if (selectedEpdIds.length === 1)
      return { mode: 'single', id: selectedEpdIds[0] };
    return { mode: 'mixed' };
  }, [selectedEpdIds]);
  const sortedEpds = useMemo(
    () => sortBy(epds, ({ name }) => name.toLowerCase()),
    [epds],
  );
  const handleEditEpd = useCallback(
    async (epd: IProduct) => {
      const product = await openEditProductDialog({
        product: epd,
        genericProductId,
      });
      if (!product) return;
      updateProductInVersionLocally(product);
    },
    [genericProductId, updateProductInVersionLocally],
  );
  const handleRemoveEpd = useCallback(
    (epd: IProduct) => {
      if (!genericProductId) return;
      const filteredIds =
        epdIdsAppliedToGenericProduct?.filter((epdId) => epdId !== epd.id) ??
        [];
      setLocallyStoredEPDIds(genericProductId, filteredIds);
      if (selection.id === epd.id) onEpdSelect({ type: 'none' });
      const unmappedEPD = { ...epd, generic_id: undefined };
      updateProductInVersion(unmappedEPD);
      if (epd.generic_id === genericProductId) updateProduct(unmappedEPD);
    },
    [
      epdIdsAppliedToGenericProduct,
      genericProductId,
      onEpdSelect,
      selection.id,
      updateProduct,
      updateProductInVersion,
    ],
  );

  const handleSelectEPD = useCallback(
    (id: ProductID) => {
      onEpdSelect({ type: 'epd', id });
      amplitudeLog(AMP_ELEMENT_APPLY_EPD, { EPDId: id });
    },
    [onEpdSelect],
  );

  const handleSelectNew = useCallback(async () => {
    const product = await openEditProductDialog({
      parent: productCategoryElement,
      genericProductId,
    });
    if (!product) return;
    handleSelectEPD(product.id);
  }, [genericProductId, handleSelectEPD, productCategoryElement]);

  const handleSelectLibrary = useCallback(async () => {
    const product = await openProductSelector();
    if (!product) return;
    handleSelectEPD(product.id);
  }, [handleSelectEPD]);

  return (
    <>
      <Menu.Item
        icon={
          selection.mode === 'none' ? (
            <Icon category="mui" icon="Check" />
          ) : undefined
        }
        selected={selection.mode === 'none'}
        onClick={() => onEpdSelect({ type: 'none' })}
      >
        None
      </Menu.Item>
      <Menu.Divider />
      {sortedEpds.map((item) => {
        const isSelected =
          selection.mode === 'single' && item.id === selection.id;
        const icon = isSelected ? (
          <Icon category="mui" icon="Check" />
        ) : undefined;
        return (
          <Menu.Item
            key={item.id}
            icon={icon}
            selected={isSelected}
            searchText={item.name}
            onClick={() => handleSelectEPD(item.id)}
          >
            {item.name}
            <Menu.Item.Action>
              <EpdKebabMenu
                epd={item}
                genericProductId={genericProductId}
                isReadonlyOrganization={isReadonlyOrganization}
                onEdit={handleEditEpd}
                onRemove={handleRemoveEpd}
              />
            </Menu.Item.Action>
          </Menu.Item>
        );
      })}
      <Menu.Divider />
      <Menu.Item
        icon={<Icon category="mui" icon="Add" />}
        onClick={handleSelectNew}
      >
        New EPD
      </Menu.Item>
      <Menu.Item onClick={handleSelectLibrary}>
        EPD Library
        <Menu.Item.RightIcon>
          <Icon category="mui" icon="KeyboardArrowRight" size="small" />
        </Menu.Item.RightIcon>
      </Menu.Item>
    </>
  );
};
